// Máquina de estados de uma corrida (docs/02 zonas + docs/03):
// idle (na zona start, timer parado) → running (saiu da start; cap 350 u/s) → finished.
// R = reset total · E = volta ao stage atual (invalida o tempo do mapa = prática).
import { START_ZONE_SPEED_CAP, ticksToMs } from '@surfrun/shared';
import type { PlayerState } from '../physics/move';
import { set, len2D } from '../physics/vec';
import type { GameMap, ZoneBox } from '../map/types';
import { HULL_HALF_XZ, HULL_HEIGHT } from '@surfrun/shared';

export type RunState = 'idle' | 'running' | 'finished';

export interface RunEvents {
  onStart?: () => void;
  onStageEnter?: (stage: number) => void;
  onFinish?: (timeMs: number, valid: boolean) => void;
  onRespawn?: () => void;
  onPush?: () => void;
}

export class Run {
  state: RunState = 'idle';
  ticks = 0;
  currentStage = 1;
  valid = true;          // false após E/goto-stage: tempo não vale (prática)
  splits: number[] = []; // ms de entrada em cada stage (2..N)
  maxSpeed = 0;

  private wasInStart = true;
  private inPushZones = new Set<ZoneBox>();

  constructor(public map: GameMap, private events: RunEvents = {}) {}

  get timeMs(): number { return ticksToMs(this.ticks); }

  /** Reset total: volta pro spawn do mapa, timer zera, tempo volta a valer. */
  resetRun(p: PlayerState, setYaw: (yaw: number) => void): void {
    this.state = 'idle';
    this.ticks = 0;
    this.currentStage = 1;
    this.valid = true;
    this.splits = [];
    this.maxSpeed = 0;
    this.wasInStart = true;
    this.teleport(p, 0, setYaw);
    this.events.onRespawn?.();
  }

  /** Volta ao início do stage atual (prática: invalida o tempo do mapa). */
  resetStage(p: PlayerState, setYaw: (yaw: number) => void): void {
    if (this.currentStage <= 1) { this.resetRun(p, setYaw); return; }
    this.valid = false;
    this.teleport(p, this.currentStage - 1, setYaw);
    this.events.onRespawn?.();
  }

  private teleport(p: PlayerState, spawnIdx: number, setYaw: (yaw: number) => void): void {
    const s = this.map.stageSpawns[spawnIdx] ?? this.map.stageSpawns[0]!;
    set(p.pos, s.pos[0], s.pos[1], s.pos[2]);
    set(p.vel, 0, 0, 0);
    p.grounded = false;
    setYaw(s.yaw);
  }

  private overlaps(p: PlayerState, z: ZoneBox): boolean {
    const h = HULL_HEIGHT;
    return (
      p.pos.x + HULL_HALF_XZ >= z.min[0] && p.pos.x - HULL_HALF_XZ <= z.max[0] &&
      p.pos.y + h >= z.min[1] && p.pos.y <= z.max[1] &&
      p.pos.z + HULL_HALF_XZ >= z.min[2] && p.pos.z - HULL_HALF_XZ <= z.max[2]
    );
  }

  /** Chamar todo tick, após playerMove. */
  update(p: PlayerState, setYaw: (yaw: number) => void): void {
    if (this.state === 'running') {
      this.ticks++;
      this.maxSpeed = Math.max(this.maxSpeed, Math.round(len2D(p.vel)));
    }

    // kill implícito por altura
    if (p.pos.y < this.map.killY) {
      this.respawnToStage(p, setYaw);
      return;
    }

    let inStart = false;
    for (const z of this.map.zones) {
      const inside = this.overlaps(p, z);
      switch (z.kind) {
        case 'start':
          if (inside) inStart = true;
          break;
        case 'end':
          if (inside && this.state === 'running') {
            this.state = 'finished';
            // atalho que pulou zona de stage: completa os splits faltantes
            while (this.splits.length < this.map.stages - 1) this.splits.push(this.timeMs);
            this.events.onFinish?.(this.timeMs, this.valid);
          }
          break;
        case 'stage':
          if (inside && this.state === 'running' && z.index && z.index > this.currentStage) {
            this.currentStage = z.index;
            // preenche splits de stages pulados por atalho (mesmo tempo)
            while (this.splits.length < z.index - 1) this.splits.push(this.timeMs);
            this.events.onStageEnter?.(z.index);
          }
          break;
        case 'kill':
          if (inside) { this.respawnToStage(p, setYaw); return; }
          break;
        case 'push':
          if (inside && !this.inPushZones.has(z) && z.vel) {
            p.vel.x += z.vel[0];
            p.vel.y = Math.max(p.vel.y, z.vel[1]);
            p.vel.z += z.vel[2];
            this.inPushZones.add(z);
            this.events.onPush?.();
          } else if (!inside) {
            this.inPushZones.delete(z);
          }
          break;
      }
    }

    // transição start → running (com cap anti-prespeed, padrão KSF)
    if (this.state === 'idle') {
      if (inStart) {
        this.wasInStart = true;
      } else if (this.wasInStart) {
        const sp = len2D(p.vel);
        if (sp > START_ZONE_SPEED_CAP) {
          const f = START_ZONE_SPEED_CAP / sp;
          p.vel.x *= f;
          p.vel.z *= f;
        }
        this.state = 'running';
        this.ticks = 0;
        this.events.onStart?.();
      }
    } else if (this.state === 'finished' && inStart) {
      // voltou pra start após terminar → nova run
      this.state = 'idle';
      this.ticks = 0;
      this.currentStage = 1;
      this.valid = true;
      this.splits = [];
      this.maxSpeed = 0;
    }
  }

  private respawnToStage(p: PlayerState, setYaw: (yaw: number) => void): void {
    if (this.currentStage <= 1 && this.state !== 'running') {
      this.teleport(p, 0, setYaw);
    } else {
      this.teleport(p, this.currentStage - 1, setYaw);
    }
    this.events.onRespawn?.();
  }
}
